import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
// Landing, StorePublic and the public marketing pages stay eager. They are
// the SEO-critical + LCP-critical routes and the crawler must render them
// without waiting on an extra chunk fetch. Everything behind auth (dashboard,
// admin, checkout, payment result screens) is code-split with React.lazy so
// the initial JS bundle for anonymous visitors is smaller — this directly
// addresses the "Pages are loading too slowly" SEO finding.
import Landing from "@/pages/Landing";
import StorePublic from "@/pages/StorePublic";
import ForGrowers from "@/pages/ForGrowers";
import FAQ from "@/pages/FAQ";
import Policies from "@/pages/Policies";
import Pricing from "@/pages/Pricing";
import SellerAuth from "@/pages/SellerAuth";
import AuthCallback from "@/pages/AuthCallback";
import ScrollToTop from "@/components/ScrollToTop";
import { useAuth } from "@/lib/useAuth";
import { subdomainSlug } from "@/lib/subdomain";

const Dashboard      = lazy(() => import("@/pages/Dashboard"));
const StoreSettings  = lazy(() => import("@/pages/StoreSettings"));
const MarketSign     = lazy(() => import("@/pages/MarketSign"));
const ShareCard      = lazy(() => import("@/pages/ShareCard"));
const AdminHome      = lazy(() => import("@/pages/AdminHome"));
const AdminSellers   = lazy(() => import("@/pages/AdminSellers"));
const AdminVouchers  = lazy(() => import("@/pages/AdminVouchers"));
const AdminWelcomeEmail = lazy(() => import("@/pages/AdminWelcomeEmail"));
const Checkout       = lazy(() => import("@/pages/Checkout"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel  = lazy(() => import("@/pages/PaymentCancel"));

// Lightweight in-app loading placeholder for lazy chunks. Silent, no spinner
// chrome — the async pages are all authenticated so they don't need marketing
// polish here.
const RouteFallback = () => (
  <div className="min-h-screen bg-[#F5F0E5]" aria-hidden="true" />
);

function Protected({ children }) {
  const { token } = useAuth();
  if (!token) return <Navigate to="/seller/login" replace />;
  return children;
}

// Preserves any deep-link path fragments (currently just an optional
// `/checkout`) when we bounce a legacy /s/:slug URL onto its canonical
// /g/:slug counterpart.
function LegacyStoreRedirect({ suffix = "" }) {
  const { slug } = useParams();
  return <Navigate to={`/g/${slug}${suffix}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  const gateSlug = subdomainSlug();

  // Emergent-managed Google auth returns to <origin>/dashboard#session_id=…
  // Detect the fragment SYNCHRONOUSLY here (not inside a useEffect) so the
  // callback beats any Protected route redirect. Works whether the user
  // kicked off Google from the apex or from a legacy subdomain Gate.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Legacy subdomain compatibility. Wildcard TLS isn't available on the
  // current plan, so `{slug}.gateandgather.co.nz` will only ever land here
  // after a user manually clicks an already-attached subdomain (e.g. an
  // individually-secured seller subdomain) or an old link that resolves via
  // a per-host record. In both cases we redirect to the canonical path-based
  // Gate URL on the apex so there's one source of truth.
  if (gateSlug) {
    const path = location.pathname === "/checkout" ? `/g/${gateSlug}/checkout` : `/g/${gateSlug}`;
    // Full-page redirect so the URL bar reflects the canonical apex host.
    if (typeof window !== "undefined") {
      window.location.replace(`https://www.gateandgather.co.nz${path}${location.search || ""}`);
      return null;
    }
    return <Navigate to={path} replace />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/seller/login" element={<SellerAuth mode="login" />} />
        <Route path="/seller/signup" element={<SellerAuth mode="signup" />} />
        <Route path="/seller/forgot" element={<SellerAuth mode="forgot" />} />
        <Route path="/seller/reset" element={<SellerAuth mode="reset" />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/dashboard/settings" element={<Protected><StoreSettings /></Protected>} />
        <Route path="/dashboard/market-sign" element={<Protected><MarketSign /></Protected>} />
        <Route path="/dashboard/share-card" element={<Protected><ShareCard /></Protected>} />
        <Route path="/admin" element={<Protected><AdminHome /></Protected>} />
        <Route path="/admin/sellers" element={<Protected><AdminSellers /></Protected>} />
        <Route path="/admin/vouchers" element={<Protected><AdminVouchers /></Protected>} />
        <Route path="/admin/welcome-email" element={<Protected><AdminWelcomeEmail /></Protected>} />
        <Route path="/for-growers" element={<ForGrowers />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/policy" element={<Navigate to="/policy/seller-policy" replace />} />
        <Route path="/policy/:slug" element={<Policies />} />
        {/* Canonical Gate routes (V1 public URL structure). */}
        <Route path="/g/:slug" element={<StorePublic />} />
        <Route path="/g/:slug/checkout" element={<Checkout />} />
        {/* Legacy /s/:slug — kept as a permanent 301-style redirect so old links,
            bookmarks and any external references keep resolving. Do NOT remove. */}
        <Route path="/s/:slug" element={<LegacyStoreRedirect />} />
        <Route path="/s/:slug/checkout" element={<LegacyStoreRedirect suffix="/checkout" />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#F5F0E5]">
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-center" richColors />
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
